import React, { Component } from "react";
import { createPortal } from "react-dom";
import { t, format_date } from "./CustomComponent.js";

// ตัวเลือก Lot ของอะไหล่ในตารางเบิกของหน้า Work Order (แทน <select> เดิม)
// เดิมทุกฟิลด์ต่อกันด้วย "/" บนบรรทัดเดียว อ่านยาก รายการยาว และแยกไม่ออกว่า hover หรือเลือกแถวไหน
// แบบใหม่ตาม mockup (Document/lot-dropdown-mockup_7.html, lot-dropdown-in-table-mockup_3.html):
// - 1 รายการ = 2 บรรทัด: รหัส Lot ตัวหนา / คลัง · ตำแหน่ง · วันหมดอายุ ตัวเล็กสีรอง + "เหลือ N" สีเทา
// - จัดกลุ่มตามคลังเอง (API ไม่ได้เรียงกลุ่มมาให้) หัวกลุ่มสีส้ม พื้นหลังสลับสีเฉพาะตอนเปลี่ยนคลัง
// - hover กับ selected สีต่างกันชัดเจน selected มีวงกลมทึบสีน้ำเงิน + เครื่องหมายถูก
// - รายการสูงไม่เกิน ~300px แล้ว scroll / ช่องที่เลือกแล้วแสดงข้อมูลครบ ไม่ใช่แค่รหัส Lot
//
// ค่าที่เลือก (value) ยังเป็นสตริงเดิม "LOT / คลัง / ตำแหน่ง" (lotItem.lot) เพราะเป็นค่าที่บันทึกลงใบงาน
// และส่งต่อให้ Q-ERP/หน้าเบิกของคลังแยกกลับด้วย split(" / ") - ห้ามเปลี่ยนรูปแบบค่านี้

const PANEL_WIDTH = 300;
const PANEL_MAX_HEIGHT = 300;
const PANEL_GAP = 2;

// แยกฟิลด์ของ lot: ผลจากปุ่มดึงสต๊อกมีฟิลด์แยกให้แล้ว ส่วน lot ที่บันทึกไว้ในใบงานมีแค่สตริงรวม ต้องแยกเอง
export const parseLotItem = (item) => {
    const [codeFromText = "", warehouseFromText = "", locationFromText = ""] = String(item?.lot ?? "").split(" / ");
    return {
        value: item?.lot ?? "",
        code: item?.lot_number || codeFromText,
        warehouse: item?.warehouse || warehouseFromText,
        location: item?.location || locationFromText,
        stock: item?.stock_quantity,
        expDate: item?.exp_date || "",
    };
};

// จัดกลุ่มตามคลัง - ลำดับกลุ่มตามคลังที่พบก่อน ภายในกลุ่มคงลำดับเดิมจาก API
const groupByWarehouse = (lots) => {
    const groups = [];
    const byWarehouse = new Map();
    lots.forEach((lot) => {
        const key = lot.warehouse || "";
        if (!byWarehouse.has(key)) {
            const group = { warehouse: key, lots: [] };
            byWarehouse.set(key, group);
            groups.push(group);
        }
        byWarehouse.get(key).lots.push(lot);
    });
    return groups;
};

const formatExp = (value) => (value ? format_date(value, "dd/mm/yyyy", "th") : "");

// บรรทัดรอง: คลัง · ตำแหน่ง · หมดอายุ dd/mm/yyyy
const metaLine = (lot) =>
    [lot.warehouse, lot.location, lot.expDate ? `${t("lot_exp_prefix")} ${formatExp(lot.expDate)}` : ""].filter(Boolean).join(" · ");

const CheckBadge = () => (
    <span className="lot-select-check" aria-hidden="true">
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6.2 5 8.6 9.5 3.6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </span>
);

const Chevron = ({ open }) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ transform: open ? "rotate(180deg)" : "none" }}>
        <path d="M3 4.5 6 7.5 9 4.5" stroke={open ? "#2563EB" : "#9099A6"} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export default class LotSelect extends Component {
    constructor(props) {
        super(props);
        this.state = { open: false, rect: null, activeIndex: -1 };
        this.triggerRef = React.createRef();
        this.panelRef = React.createRef();
        this.scrollRef = React.createRef();
    }

    componentWillUnmount() {
        this.detachListeners();
    }

    // เรียงตามกลุ่มคลังไว้ตั้งแต่ต้น - ลำดับเดียวกับที่แสดงบนจอ ปุ่มลูกศรขึ้น/ลงจึงเดินตามที่ผู้ใช้เห็น
    getLots = () => groupByWarehouse((this.props.lots || []).map(parseLotItem)).flatMap((group) => group.lots);

    open = () => {
        if (this.props.disabled || !this.getLots().length) return;
        const lots = this.getLots();
        const selectedIndex = lots.findIndex((lot) => lot.value === this.props.value);
        this.setState({ open: true, rect: this.triggerRef.current?.getBoundingClientRect() || null, activeIndex: selectedIndex });
        document.addEventListener("mousedown", this.handleOutside, true);
        // ตารางอยู่ใน modal ที่เลื่อนได้ - ตำแหน่งแผงต้องตามช่องที่กดไปด้วย
        window.addEventListener("scroll", this.reposition, true);
        window.addEventListener("resize", this.reposition);
    };

    close = () => {
        this.detachListeners();
        this.setState({ open: false, activeIndex: -1 });
    };

    detachListeners = () => {
        document.removeEventListener("mousedown", this.handleOutside, true);
        window.removeEventListener("scroll", this.reposition, true);
        window.removeEventListener("resize", this.reposition);
    };

    toggle = () => (this.state.open ? this.close() : this.open());

    reposition = (e) => {
        // scroll ภายในรายการเองไม่ต้องขยับแผง
        if (e && this.scrollRef.current && this.scrollRef.current.contains(e.target)) return;
        if (!this.triggerRef.current) return;
        this.setState({ rect: this.triggerRef.current.getBoundingClientRect() });
    };

    handleOutside = (e) => {
        if (this.triggerRef.current?.contains(e.target) || this.panelRef.current?.contains(e.target)) return;
        this.close();
    };

    select = (lot) => {
        this.close();
        if (lot.value !== this.props.value) this.props.onChange?.(lot.value);
        this.triggerRef.current?.focus();
    };

    handleKeyDown = (e) => {
        const lots = this.getLots();
        if (!this.state.open) {
            if (["Enter", " ", "ArrowDown"].includes(e.key)) {
                e.preventDefault();
                this.open();
            }
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            this.close();
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const step = e.key === "ArrowDown" ? 1 : -1;
            const next = Math.min(Math.max(this.state.activeIndex + step, 0), lots.length - 1);
            this.setState({ activeIndex: next }, () => {
                this.scrollRef.current?.querySelector(`[data-lot-index="${next}"]`)?.scrollIntoView({ block: "nearest" });
            });
        } else if (e.key === "Enter" && lots[this.state.activeIndex]) {
            e.preventDefault();
            this.select(lots[this.state.activeIndex]);
        } else if (e.key === "Tab") {
            this.close();
        }
    };

    // แผงลอยด้วย position:fixed ผ่าน portal - ตารางอะไหล่อยู่ในกล่องที่ตัดขอบ (overflow) แผงแบบ absolute จะโดนตัด
    // ไม่พอที่ด้านล่างจะเปิดขึ้นด้านบนแทน / ชิดขอบขวาของจอถ้าล้น
    getPanelStyle = () => {
        const { rect } = this.state;
        if (!rect) return { display: "none" };
        const width = Math.max(PANEL_WIDTH, rect.width);
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < PANEL_MAX_HEIGHT + 16 && rect.top > spaceBelow;
        const left = Math.min(rect.left, window.innerWidth - width - 8);
        return {
            position: "fixed",
            left: Math.max(8, left),
            width,
            ...(openUp ? { bottom: window.innerHeight - rect.top + PANEL_GAP } : { top: rect.bottom + PANEL_GAP }),
        };
    };

    renderPanel() {
        const lots = this.getLots();
        const groups = groupByWarehouse(lots);
        const { value } = this.props;
        const { activeIndex } = this.state;
        let rowIndex = -1;

        return createPortal(
            <div ref={this.panelRef} className="lot-select-panel" style={this.getPanelStyle()} role="listbox">
                <div ref={this.scrollRef} className="lot-select-scroll" style={{ maxHeight: PANEL_MAX_HEIGHT }}>
                    {groups.map((group, groupIndex) => (
                        <div key={`${group.warehouse}-${groupIndex}`} className="lot-select-group">
                            <div className="lot-select-group-label">{group.warehouse || t("lot_no_warehouse")}</div>
                            {group.lots.map((lot) => {
                                rowIndex += 1;
                                const index = lots.indexOf(lot);
                                const selected = lot.value === value;
                                const className = [
                                    "lot-select-row",
                                    // สลับพื้นหลังตามกลุ่มคลัง (ไม่ใช่ zebra ทีละแถว)
                                    groupIndex % 2 === 0 ? "tint-a" : "tint-b",
                                    selected ? "selected" : "",
                                    index === activeIndex ? "active" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ");
                                return (
                                    <div
                                        key={lot.value || rowIndex}
                                        data-lot-index={index}
                                        className={className}
                                        role="option"
                                        aria-selected={selected}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => this.select(lot)}
                                    >
                                        <div className="lot-select-main">
                                            <div className="lot-select-code">{lot.code || lot.value}</div>
                                            <div className="lot-select-meta">{metaLine(lot)}</div>
                                        </div>
                                        <div className="lot-select-side">
                                            <span className="lot-select-qty">
                                                {t("remaining")} {lot.stock ?? 0}
                                            </span>
                                            {selected && <CheckBadge />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>,
            document.body
        );
    }

    render() {
        const { value, disabled, placeholder } = this.props;
        const { open } = this.state;
        const lots = this.getLots();
        // ค่าที่เลือกอาจไม่อยู่ในรายการล่าสุด (เช่นแถวที่ล็อกแล้ว) ก็ยังแสดงจากสตริงที่บันทึกไว้ได้
        const selected = value ? lots.find((lot) => lot.value === value) || parseLotItem({ lot: value }) : null;
        const isDisabled = disabled || !lots.length;
        const fullText = selected
            ? [selected.code, metaLine(selected), selected.stock != null ? `${t("remaining")} ${selected.stock}` : ""].filter(Boolean).join(" · ")
            : "";

        return (
            <div className="lot-select">
                <button
                    type="button"
                    ref={this.triggerRef}
                    className={["lot-select-trigger", open ? "open" : "", selected ? "" : "empty"].filter(Boolean).join(" ")}
                    disabled={isDisabled}
                    title={fullText || undefined}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    onClick={this.toggle}
                    onKeyDown={this.handleKeyDown}
                >
                    <span className="lot-select-value">
                        {selected ? (
                            <>
                                <span className="lot-select-code">{selected.code || selected.value}</span>
                                <span className="lot-select-meta">
                                    {[metaLine(selected), selected.stock != null ? `${t("remaining")} ${selected.stock}` : ""]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </span>
                            </>
                        ) : (
                            <span className="lot-select-placeholder">{placeholder}</span>
                        )}
                    </span>
                    <Chevron open={open} />
                </button>
                {open && this.renderPanel()}
            </div>
        );
    }
}
